import React, { useState, useEffect, useCallback } from 'react';
import { Title } from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    CircularProgress,
    Alert,
    TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { getApiUrl } from '../utils/apiUrl';
import QRScanner from './QRScanner';

const apiUrl = getApiUrl();

const getToken = () => localStorage.getItem('admin_token');

const authHeaders = () => {
    const token = getToken();
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

interface HOA {
    uuid: string;
    name: string;
    boundary: any[] | null;
    created_at: string | null;
    updated_at: string | null;
}

interface Home {
    uuid: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
    property_type: string | null;
    year_built: string | null;
    bedrooms: string | null;
    bathrooms: string | null;
}

// --- Create HOA Modal ---

const CreateHoaModal: React.FC<{ onClose: () => void; onCreated: () => void }> = ({
    onClose,
    onCreated,
}) => {
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleCreate = async () => {
        if (!name.trim()) return;
        setSaving(true);
        setError('');
        try {
            const response = await fetch(`${apiUrl}/api/v1/hoas/`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ name: name.trim() }),
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.detail || 'Failed to create HOA');
            }
            onCreated();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create HOA');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1300,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: '#1e1e1e',
                    borderRadius: '8px',
                    padding: '24px',
                    maxWidth: '450px',
                    width: '90%',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    border: '1px solid #333',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px',
                    }}
                >
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 500, color: '#ffffff' }}>
                        Create New HOA
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#9e9e9e',
                            padding: '4px',
                            lineHeight: 1,
                        }}
                    >
                        ×
                    </button>
                </div>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <TextField
                    label="HOA Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    required
                    autoFocus
                    sx={{
                        mb: 3,
                        '& .MuiInputBase-input': { color: '#fff' },
                        '& .MuiInputLabel-root': { color: '#9e9e9e' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#90caf9' },
                    }}
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={!name.trim() || saving}
                        startIcon={saving ? <CircularProgress size={18} /> : <AddIcon />}
                    >
                        {saving ? 'Creating...' : 'Create'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// --- Add Home to HOA Modal ---

const AddHomeModal: React.FC<{
    hoaUuid: string;
    existingHomeUuids: string[];
    onClose: () => void;
    onAdded: () => void;
}> = ({ hoaUuid, existingHomeUuids, onClose, onAdded }) => {
    const [search, setSearch] = useState('');
    const [results, setResults] = useState<Home[]>([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState<string | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (search.trim().length >= 2) {
                const query = search.trim();
                setLoading(true);
                setError('');
                fetch(
                    `${apiUrl}/api/v1/admin/homes?search=${encodeURIComponent(query)}&limit=20`,
                    { headers: authHeaders() },
                )
                    .then((r) => {
                        if (!r.ok) throw new Error('Failed to search homes');
                        return r.json();
                    })
                    .then((data) => {
                        const items: Home[] = data.items || [];
                        setResults(items.filter((h) => !existingHomeUuids.includes(h.uuid)));
                    })
                    .catch((err) => {
                        setError(err instanceof Error ? err.message : 'Search failed');
                    })
                    .finally(() => setLoading(false));
            } else {
                setResults([]);
            }
        }, 300);
        return () => clearTimeout(timeout);
    }, [search, existingHomeUuids]);

    const handleAdd = async (homeUuid: string) => {
        setAdding(homeUuid);
        setError('');
        try {
            const response = await fetch(`${apiUrl}/api/v1/hoas/${hoaUuid}/homes/${homeUuid}`, {
                method: 'POST',
                headers: authHeaders(),
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.detail || 'Failed to add home');
            }
            onAdded();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add home');
        } finally {
            setAdding(null);
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1300,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: '#1e1e1e',
                    borderRadius: '8px',
                    padding: '24px',
                    maxWidth: '550px',
                    width: '90%',
                    maxHeight: '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    border: '1px solid #333',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px',
                    }}
                >
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 500, color: '#ffffff' }}>
                        Add Home to HOA
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#9e9e9e',
                            padding: '4px',
                            lineHeight: 1,
                        }}
                    >
                        ×
                    </button>
                </div>

                <TextField
                    label="Search by address, city, or state"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth
                    autoFocus
                    size="small"
                    sx={{
                        mb: 2,
                        '& .MuiInputBase-input': { color: '#fff' },
                        '& .MuiInputLabel-root': { color: '#9e9e9e' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#90caf9' },
                    }}
                />

                {error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}

                <div style={{ overflow: 'auto', flex: 1 }}>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {!loading && search.trim().length >= 2 && results.length === 0 && (
                        <Typography color="#9e9e9e" sx={{ p: 2, textAlign: 'center' }}>
                            No matching homes found.
                        </Typography>
                    )}

                    {!loading && search.trim().length < 2 && (
                        <Typography color="#9e9e9e" sx={{ p: 2, textAlign: 'center' }}>
                            Type at least 2 characters to search.
                        </Typography>
                    )}

                    {results.map((home) => (
                        <div
                            key={home.uuid}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px 12px',
                                borderBottom: '1px solid #333',
                            }}
                        >
                            <div>
                                <div style={{ color: '#ffffff', fontSize: '14px' }}>
                                    {home.address}
                                </div>
                                <div style={{ color: '#9e9e9e', fontSize: '12px' }}>
                                    {home.city}, {home.state} {home.zip_code}
                                </div>
                            </div>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleAdd(home.uuid)}
                                disabled={adding === home.uuid}
                            >
                                {adding === home.uuid ? 'Adding...' : 'Add'}
                            </Button>
                        </div>
                    ))}
                </div>

                <div style={{ marginTop: '16px', textAlign: 'right' }}>
                    <Button onClick={onClose}>Close</Button>
                </div>
            </div>
        </div>
    );
};

// --- Link Trusted Sign to HOA Dialog ---

type LinkResultStatus = 'linking' | 'linked' | 'created' | 'already_linked' | 'error';

const LinkTrustedSignDialog: React.FC<{
    hoaUuid: string;
    open: boolean;
    onClose: () => void;
}> = ({ hoaUuid, open, onClose }) => {
    const [lastResult, setLastResult] = useState<
        | { uuid: string; status: LinkResultStatus; message: string }
        | null
    >(null);
    const [linkedCount, setLinkedCount] = useState(0);

    useEffect(() => {
        if (!open) {
            setLastResult(null);
            setLinkedCount(0);
        }
    }, [open]);

    const handleScanSuccess = async (uuid: string) => {
        setLastResult({ uuid, status: 'linking', message: 'Linking sign...' });
        try {
            const response = await fetch(
                `${apiUrl}/api/v1/admin/hoas/${hoaUuid}/signs/${uuid}`,
                { method: 'POST', headers: authHeaders() },
            );
            if (response.ok) {
                const data = await response.json().catch(() => ({}));
                if (data.already_linked) {
                    setLastResult({
                        uuid,
                        status: 'already_linked',
                        message: 'Sign already linked to this HOA',
                    });
                } else if (data.created) {
                    setLastResult({
                        uuid,
                        status: 'created',
                        message: 'Sign created and linked to HOA',
                    });
                    setLinkedCount((c) => c + 1);
                } else {
                    setLastResult({
                        uuid,
                        status: 'linked',
                        message: 'Sign linked to HOA',
                    });
                    setLinkedCount((c) => c + 1);
                }
            } else {
                const data = await response.json().catch(() => ({}));
                setLastResult({
                    uuid,
                    status: 'error',
                    message: data.detail || 'Failed to link sign',
                });
            }
        } catch (err) {
            setLastResult({
                uuid,
                status: 'error',
                message: err instanceof Error ? err.message : 'Network error',
            });
        }
    };

    const severityFor = (s: LinkResultStatus): 'success' | 'info' | 'error' => {
        if (s === 'linked' || s === 'created') return 'success';
        if (s === 'already_linked' || s === 'linking') return 'info';
        return 'error';
    };

    const resultBanner = lastResult ? (
        <Box sx={{ width: '100%' }}>
            <Alert severity={severityFor(lastResult.status)} sx={{ mb: 1 }}>
                {lastResult.message}
            </Alert>
            <Typography
                variant="caption"
                sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all', color: 'text.secondary' }}
            >
                {lastResult.uuid}
            </Typography>
            {linkedCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {linkedCount} sign{linkedCount !== 1 ? 's' : ''} linked this session
                </Typography>
            )}
        </Box>
    ) : (
        <Typography variant="caption" color="text.secondary">
            Point the camera at a sign QR code to link it to {`this HOA`}.
        </Typography>
    );

    return (
        <QRScanner
            open={open}
            continuous
            onScanSuccess={handleScanSuccess}
            onClose={onClose}
            extraContent={resultBanner}
        />
    );
};

// --- Homes List for a selected HOA ---

const HoaHomesList: React.FC<{ hoa: HOA; onBack: () => void }> = ({ hoa, onBack }) => {
    const [homes, setHomes] = useState<Home[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showLinkSignModal, setShowLinkSignModal] = useState(false);
    const [removing, setRemoving] = useState<string | null>(null);

    const removeHome = async (homeUuid: string) => {
        setRemoving(homeUuid);
        try {
            const response = await fetch(`${apiUrl}/api/v1/hoas/${hoa.uuid}/homes/${homeUuid}`, {
                method: 'DELETE',
                headers: authHeaders(),
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.detail || 'Failed to remove home');
            }
            setHomes((prev) => prev.filter((h) => h.uuid !== homeUuid));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to remove home');
        } finally {
            setRemoving(null);
        }
    };

    const fetchHomes = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${apiUrl}/api/v1/hoas/${hoa.uuid}/homes`, {
                headers: authHeaders(),
            });
            if (!response.ok) throw new Error('Failed to fetch homes');
            const data = await response.json();
            setHomes(Array.isArray(data) ? data : data.items || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load homes');
        } finally {
            setLoading(false);
        }
    }, [hoa.uuid]);

    useEffect(() => {
        fetchHomes();
    }, [fetchHomes]);

    return (
        <Box>
            <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
                Back to HOAs
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                    {hoa.name} — Homes
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<QrCodeScannerIcon />}
                        onClick={() => setShowLinkSignModal(true)}
                    >
                        Link Trusted Sign
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setShowAddModal(true)}
                    >
                        Add Home
                    </Button>
                </Box>
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && homes.length === 0 && (
                <Typography color="text.secondary" sx={{ p: 2 }}>
                    No homes in this HOA.
                </Typography>
            )}

            {!loading && !error && homes.length > 0 && (
                <Card>
                    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid rgba(224,224,224,1)' }}>Address</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid rgba(224,224,224,1)' }}>City</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid rgba(224,224,224,1)' }}>State</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid rgba(224,224,224,1)' }}>Zip</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid rgba(224,224,224,1)' }}>Type</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid rgba(224,224,224,1)' }}>Year Built</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid rgba(224,224,224,1)' }}>Bed</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid rgba(224,224,224,1)' }}>Bath</th>
                                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: 500, borderBottom: '1px solid rgba(224,224,224,1)' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {homes.map((home) => (
                                    <tr key={home.uuid} style={{ borderBottom: '1px solid rgba(224,224,224,0.5)' }}>
                                        <td style={{ padding: '12px' }}>{home.address}</td>
                                        <td style={{ padding: '12px' }}>{home.city}</td>
                                        <td style={{ padding: '12px' }}>{home.state}</td>
                                        <td style={{ padding: '12px' }}>{home.zip_code}</td>
                                        <td style={{ padding: '12px' }}>{home.property_type || '-'}</td>
                                        <td style={{ padding: '12px' }}>{home.year_built || '-'}</td>
                                        <td style={{ padding: '12px' }}>{home.bedrooms || '-'}</td>
                                        <td style={{ padding: '12px' }}>{home.bathrooms || '-'}</td>
                                        <td style={{ padding: '12px', textAlign: 'right' }}>
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() => removeHome(home.uuid)}
                                                disabled={removing === home.uuid}
                                            >
                                                {removing === home.uuid ? 'Removing...' : 'Remove'}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}

            {showAddModal && (
                <AddHomeModal
                    hoaUuid={hoa.uuid}
                    existingHomeUuids={homes.map((h) => h.uuid)}
                    onClose={() => setShowAddModal(false)}
                    onAdded={() => {
                        setShowAddModal(false);
                        fetchHomes();
                    }}
                />
            )}

            <LinkTrustedSignDialog
                hoaUuid={hoa.uuid}
                open={showLinkSignModal}
                onClose={() => setShowLinkSignModal(false)}
            />
        </Box>
    );
};

// --- Main HOA Admin Page ---

const HoaAdmin: React.FC = () => {
    const [hoas, setHoas] = useState<HOA[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedHoa, setSelectedHoa] = useState<HOA | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const fetchHoas = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${apiUrl}/api/v1/hoas/?skip=0&limit=100`, {
                headers: authHeaders(),
            });
            if (!response.ok) throw new Error('Failed to fetch HOAs');
            const data = await response.json();
            setHoas(Array.isArray(data) ? data : data.items || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load HOAs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHoas();
    }, []);

    if (selectedHoa) {
        return (
            <Box sx={{ p: 3 }}>
                <Title title={`HOA - ${selectedHoa.name}`} />
                <HoaHomesList hoa={selectedHoa} onBack={() => setSelectedHoa(null)} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Title title="HOAs" />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">HOAs</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreateModal(true)}
                >
                    Create New HOA
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && !error && hoas.length === 0 && (
                <Typography color="text.secondary" sx={{ p: 2 }}>
                    No HOAs found.
                </Typography>
            )}

            {!loading && !error && hoas.length > 0 && (
                <Card>
                    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid rgba(224,224,224,1)' }}>Name</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid rgba(224,224,224,1)' }}>Created</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 500, borderBottom: '1px solid rgba(224,224,224,1)' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {hoas.map((hoa) => (
                                    <tr
                                        key={hoa.uuid}
                                        style={{ cursor: 'pointer', borderBottom: '1px solid rgba(224,224,224,0.5)' }}
                                        onClick={() => setSelectedHoa(hoa)}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                    >
                                        <td style={{ padding: '12px 16px' }}>{hoa.name}</td>
                                        <td style={{ padding: '12px 16px', opacity: 0.7 }}>
                                            {hoa.created_at ? new Date(hoa.created_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                            <Button size="small" onClick={(e) => { e.stopPropagation(); setSelectedHoa(hoa); }}>
                                                View Homes
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}

            {showCreateModal && (
                <CreateHoaModal
                    onClose={() => setShowCreateModal(false)}
                    onCreated={() => {
                        setShowCreateModal(false);
                        fetchHoas();
                    }}
                />
            )}
        </Box>
    );
};

export default HoaAdmin;
